import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { withRouter } from "tools/routerCompat";
import { MSplitter, Button } from "components/Shared";
import { Control, DataField, Enumeration, EnumerationItem, FormDefinition, FormDefinitionValidateResponse, FormDesignerPromoteResponse, Question, Section } from "interfaces/Phoenix.FormsDesigner.Models";
import {
  getFormDefinition,
  updateFormDefinition,
  validateFormDefinition,
  validateFormDefinitionExportId,
  generateDataModel as generateDataModelRequest,
  updateDataModel as updateDataModelRequest,
  getPromoteData,
  promoteForm,
} from "requests/phoenixForms";
import LoadingCover from "components/Shared/LoadingCover";
import PhoenixFormDesignerPreview from "./PhoenixFormDesignerPreview";
import PhoenixFormDesignerProps from "./PhoenixFormDesignerProps";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import uuid from "uuid/v4";
import NewQuestionDialog, { StagedQuestionsReference } from "./NewQuestionDialog";
import { loadPreference, savePreference } from "requests/preferences";
import { PhoenixFormsDesignerAddQuestionOrder } from "typeConstants/preferenceTypes";
import CodeEditor, { ensureAceLoaded } from "./CodeEditor/CodeEditor";
import { createSelectGroupEnumItemId, deepCopy, isQuestionSelectGroup } from "./helpers";
import responseHandler from "responseHandler";
import { connect } from "react-redux";
import { setCoreLayoutHeaderVisibility } from "actions/layout";
import ValidationMessages from "./ValidationMessages";
import { getTopicsList } from "requests/cms";
import { phoenixProjectId } from "components/CMS/ContentManagementList";
import { TopicResponse } from "requests/cms/interfaces";
import WebConfig from "middlewares/WebConfig";
import Select from "react-select";
import { PromoteFormData } from "interfaces/Mueller.Business.Models";
import { designerQuestionDefs } from "./DesignerQuestionDefinitions";

type ToastType = "success" | "warning" | "error";

interface Props {
  match: { params: { phoenixFormId: string } },
  makeToast: (text: string, type: ToastType) => void,
  setHeaderVisibility: (visible: boolean) => void,

  // router
  history: any,
};

interface QuestionEditDetails {
  section?: Section;
  repeatOrGroup?: Question;
  modifierQuestionId?: string; // on adds, if specified this is the question id we are adding below. On remove, this is the question id we are deleting
};

interface DesignerEditFunctions {
  addNewQuestion: (details: QuestionEditDetails) => void;
  deleteQuestion: (questionId: string) => void;
  reorderSections: (newSectionIndexOrder: number[]) => void;
  reorderQuestions: (details: QuestionEditDetails, newQuestionIdOrder: string[]) => void;
  addNewSection: (belowSectionIndex: number, newSectionTitle?: string) => void;
  renameSection: (sectionIndex: number, sectionTitle: string) => void;
  deleteSection: (sectionIndex: number) => void;
  saveFormDef: () => void;
  addListItemToQuestion: (questionId: string, name: string, value: string, xpath?: string, exportId?: string, conditionalDisplay?: string, selectedCondition?: string) => void;
  updateQuestionListItem: (questionId: string, listEditIndex: number, newText: string, newValue: string, xpath?: string, exportId?: string, bindableId?: string, conditionalDisplay?: string, selectedCondition?: string) => void;
  setQuestionListItems: (questionId: string, items: EnumerationItem[]) => void;
  reorderQuestionListItems: (questionId: string, newOrder: string[]) => void;
  deleteListItemFromQuestion: (questionId: string, index: number) => void;
};

interface DesignerClipboardFunctions {
  data: Question[];
  copy: (questionId: string) => void;
  remove: (clipboardIndex: number) => void;
};

export interface DesignerProviderValue {
  phoenixFormId: string; 
  formDefinition: FormDefinition;
  getQuestionById: (id: string) => Question;
  setSelectedQuestion: (question: Question) => void;
  selectedQuestion: Question;
  refreshFormDef: (lastChangeType: FormsDesignerLastChangeType) => void;
  formDefinitionUpdatedAt: number;
  editFns: DesignerEditFunctions;
  clipboard: DesignerClipboardFunctions;
  addQuestionOrder: string[];
  setAddQuestionOrder: (order: string[]) => void,
  lastChangeType: FormsDesignerLastChangeType,
  lastChangeData: any,
  isSavingForm: boolean,
  cmsList: TopicResponse[],
  generateNewDataModel: () => void,
  updateDataModel: () => void,
};

export const PhoenixFormsDesignerContext = React.createContext<DesignerProviderValue>(null);

export type FormsDesignerLastChangeType = 
  "initial load" |
  "form saved" |
  "add section" | // last change data is where the section was added, this is used to properly update what sections are hidden/shown. sadly we have no section ids to go off of so we use... index
  "rename section" |
  "delete section" | // last change data is also index here
  "add question" |
  "delete question" |
  "move section" |
  "move question"|
  "control field edited" |
  "control list item added" |
  "control list item edited" |
  "control list items changed" |
  "control list reordered" |
  "control list item deleted" |
  "generated data model" |
  "updated data model";

const PhoenixFormDesigner = ({ match: { params: { phoenixFormId } }, makeToast, setHeaderVisibility, history }: Props) => {
  const mounted = useRef(true);
  const contextRef = useRef<DesignerProviderValue>(null);

  const dialog = useContext<DialogProviderValue>(DialogContext);

  // theres no point in keeping the form def in state when you arent supposed to mutate state
  // we would just be:
  //    copying it over
  //    mutating it (which would mutate state anyways unless we did a deep copy which would be a huge waste of time)
  //    and then re setting state with it
  //
  // So instead we just save it as a ref, and then we use the formDefinitionUpdatedAt to let any children be able to listen for formDef changes, and update child components when form def is changed
  const _formDefRef = useRef<FormDefinition>(null);
  const formDefinition = _formDefRef.current;
  const [formDefinitionUpdatedAt, _setFormDefinitionUpdatedAt] = useState<number>();
  const [formLastLoadedAt, setFormLastLoadedAt] = useState<number>(); // key on this to force update some stuff when we literally fully change the entire data model
  const [lastChangeType, setLastChangeType] = useState<FormsDesignerLastChangeType>(null);
  const [lastChangeData, setLastChangeData] = useState<any>();
  const [isSavingForm, setIsSavingForm] = useState<boolean>(false);
  const [genericLoadingMessage, setGenericLoadingMessage] = useState<string>(null);
  const [clipboard, setClipboard] = useState<Question[]>((window as any).formsDesginerClipboard ?? []);
  const historyBlockRef = useRef<Function>(null);
  const [cmsList, setCmsList] = useState<TopicResponse[]>();
  const [collapsedButtons, setCollapsedButtons] = useState<boolean>();
  const headerRef = useRef<HTMLDivElement>(null);
  const headerResize = useRef<ResizeObserver>(null);

  const refreshFormDef = (lastChangeType: FormsDesignerLastChangeType, lastChangeData: any = null) => {
    setLastChangeType(lastChangeType);
    setLastChangeData(lastChangeData);
    _setFormDefinitionUpdatedAt(Date.now());
  }

  const [selectedQuestion, setSelectedQuestion] = useState<Question>();
  const [addQuestionOrder, _setAddQuestionOrder] = useState<string[]>(null);

  useEffect(() => {
    setHeaderVisibility(false);

    getFormDefinition(phoenixFormId, formDef => {
      _formDefRef.current = formDef;
      setFormLastLoadedAt(Date.now());
      refreshFormDef("initial load");
    }, null, mounted);

    loadPreference(PhoenixFormsDesignerAddQuestionOrder, pref => {
      if (pref.value?.length > 0) _setAddQuestionOrder(pref.value.split(","));
      else _setAddQuestionOrder([]);
    }, mounted);

    getTopicsList(phoenixProjectId, setCmsList, mounted);

    ensureAceLoaded();

    return () => {
      mounted.current = false;
      setHeaderVisibility(true);
      clearLeavePageBlock();
    };
  }, []);

  const clearLeavePageBlock = () => {
    // @ts-ignore
    window.onbeforeunload = null;
    historyBlockRef.current?.();
    historyBlockRef.current = null;
  };

  useEffect(() => {
    if (formHasUnsavedChanges()) {
      // @ts-ignore
      window.onbeforeunload = () => true;
      if (!historyBlockRef.current) {
        historyBlockRef.current = history.block(newLocation => {
          if (location.pathname !== newLocation.pathname) {
            return "You have unsaved changes. Are you sure you want to leave this page?";
          }
        });
      }
    } else {
      clearLeavePageBlock();
    }
  }, [lastChangeType]);
  
  const formHasUnsavedChanges = () => {
    return lastChangeType !== "initial load" && lastChangeType !== "form saved" && lastChangeType;
  }

  const whereIsThisQuestion = (questionId: string): { section?: Section, repeatOrGroup?: Question } => {
    let section = formDefinition.pages[0].sections.find(s => s.mobileQuestions.indexOf(questionId) > -1);
    if (section) return { section };

    let repeatOrGroup = formDefinition.questionLibrary.questions.find(q => q.control.controls?.some(c => c.questionId === questionId));
    return { repeatOrGroup };
  };

  const addNewQuestion = (details: QuestionEditDetails, stagedQuestionsReference?: StagedQuestionsReference) => {
    if (stagedQuestionsReference) {
      if (!stagedQuestionsReference.questions?.length && !stagedQuestionsReference.clipboardQuestions?.length) {
        dialog.hide();
        return;
      }

      let idsToAdd = stagedQuestionsReference.questions.map(q => {
        let question = {
          id: uuid(),
          control: deepCopy(q),
          description: "",
          name: "",
        } as Question;

        if (!question.control.dataField) question.control.dataField = {} as DataField;

        let control = question.control;
        if (!control.label) control.label = "";

        if (control.type.startsWith("DynamicItemset") || control.type.startsWith("AdvancedDynamicItemset")) {
          control.dataField.enumeration = {
            id: uuid(),
            items: [{
              name: "",
              value: "",
              xpath: ""
            } as EnumerationItem],
          } as Enumeration;
        } else if (control.type === "SelectOne" || control.type === "SelectMany" || control.type === "SelectGroup" || control.type == "Photo" || control.type == "Ruleset" || control.type == "Recommendation") {
          control.dataField.enumeration = {
            id: uuid(),
            items: [],
          } as Enumeration;
        }

        formDefinition.questionLibrary.questions.push(question);
        return question.id;
      });

      idsToAdd.push(...stagedQuestionsReference.clipboardQuestions.map(q => {
        let question = {
          ...deepCopy(q),
          id: uuid(),
        } as Question;

        formDefinition.questionLibrary.questions.push(question);
        return question.id;
      }))
      
      let insertIndex: number;

      if (details.repeatOrGroup) {
        if (details.modifierQuestionId) insertIndex = details.repeatOrGroup.control.controls.findIndex(c => c.questionId === details.modifierQuestionId) + 1;
        else insertIndex = details.repeatOrGroup.control.controls.length;

        details.repeatOrGroup.control.controls.splice(insertIndex, 0, ...idsToAdd.map(id => ({
          questionId: id,
          type: "QuestionReference",
        } as Control)));
      }

      if (details.section) {
        if (details.modifierQuestionId) insertIndex = details.section.mobileQuestions.findIndex(q => q === details.modifierQuestionId) + 1;
        else insertIndex = details.section.mobileQuestions.length;

        details.section.mobileQuestions.splice(insertIndex, 0, ...idsToAdd);
      }
      
      refreshFormDef("add question");
      dialog.hide();
    } else {
      let stagedQuestionsReference: StagedQuestionsReference = { questions: [], clipboardQuestions: [] };

      dialog.showYesNo(
        null,
        <NewQuestionDialog
          stagedQuestionsReference={stagedQuestionsReference}
          context={contextRef}
          filterOutContainerControls={!!details.repeatOrGroup}
          questionDefinitions={designerQuestionDefs}
        />,
        () => addNewQuestion(details, stagedQuestionsReference),
        dialog.hide,
        "Add Questions",
        "Cancel",
        { className: "new-question-dialog", useFlexInsteadOfTransformForCentering: true },
      );
    }
  };
  
  const deleteQuestion = (questionId: string, areYouSure: boolean = false) => {
    let question = getQuestionById(questionId);
    if (!areYouSure) {
      dialog.showYesNo("Delete Section", `Are you sure you want to delete question "${question.control.label}"?`, () => deleteQuestion(questionId, true));
    } else {
      let deleteIndex = formDefinition.questionLibrary.questions.findIndex(q => q.id === questionId);
      let deletedQuestion = formDefinition.questionLibrary.questions.splice(deleteIndex, 1)[0];
      
      // need to clean up the questions that might be inside of this group/repeat
      let nestedQuestionIds = deletedQuestion.control.controls?.map(c => c.questionId);
      if (nestedQuestionIds?.length > 0) {
        formDefinition.questionLibrary.questions = formDefinition.questionLibrary.questions.filter(q => !nestedQuestionIds.some(nq => q.id === nq));
      }

      const where = whereIsThisQuestion(questionId);

      if (where.repeatOrGroup) {
        let deleteIndex = where.repeatOrGroup.control.controls.findIndex(c => c.questionId === questionId);
        where.repeatOrGroup.control.controls.splice(deleteIndex, 1);
      }

      if (where.section) {
        let deleteIndex = where.section.mobileQuestions.findIndex(q => q === questionId);
        where.section.mobileQuestions.splice(deleteIndex, 1);
      }

      refreshFormDef("delete question");
    }
  };

  // this isnt used anymore, we just setQuestionListItems instead
  const addListItemToQuestion = (questionId: string, name: string, value: string, xpath?: string, exportId?: string, conditionalDisplay?: string, selectedCondition?: string) => {
    let question = getQuestionById(questionId);

    let bindableId: string = undefined;

    if (isQuestionSelectGroup(question)) bindableId = createSelectGroupEnumItemId(question.control.dataField?.bindableId, name);

    question.control.dataField.enumeration.items.push({ name, value: value ?? name, xpath, exportId, bindableId, relevantExpression: conditionalDisplay, selectedExpression: selectedCondition } as EnumerationItem);
    refreshFormDef("control list item added");
  };

  const updateQuestionListItem = (questionId: string, listEditIndex: number, newText: string, newValue: string, xpath?: string, exportId?: string, bindableId?: string, conditionalDisplay?: string, selectedCondition?: string) => {
    let question = getQuestionById(questionId);
    question.control.dataField.enumeration.items[listEditIndex].name = newText;
    question.control.dataField.enumeration.items[listEditIndex].value = newValue;
    question.control.dataField.enumeration.items[listEditIndex].xpath = xpath;
    question.control.dataField.enumeration.items[listEditIndex].exportId = exportId;
    question.control.dataField.enumeration.items[listEditIndex].bindableId = bindableId;
    question.control.dataField.enumeration.items[listEditIndex].relevantExpression = conditionalDisplay;
    question.control.dataField.enumeration.items[listEditIndex].selectedExpression = selectedCondition;
    refreshFormDef("control list item edited");
  };

  const setQuestionListItems = (questionId: string, items: EnumerationItem[]) => {
    let question = getQuestionById(questionId);
    question.control.dataField.enumeration.items = items;
    refreshFormDef("control list items changed");
  };

  const reorderQuestionListItems = (questionId: string, newOrder: string[]) => {
    let question = getQuestionById(questionId);
    question.control.dataField.enumeration.items = newOrder.map(index => question.control.dataField.enumeration.items[parseInt(index)]);
    refreshFormDef("control list reordered");
  };

  const deleteListItemFromQuestion = (questionId: string, index: number) => {
    let question = getQuestionById(questionId);
    question.control.dataField.enumeration.items.splice(index, 1);
    refreshFormDef("control list item deleted");
  };

  const addNewSection = (belowSectionIndex: number, newSectionTitle?: string) => {
    if (!newSectionTitle) {
      dialog.prompt({
        title: "New Section",
        message: "Enter a name for the new section",
        okLabel: "Add Section",
        onOkClicked: name => addNewSection(belowSectionIndex, name),
      });
    } else {
      let sections = formDefinition.pages[0].sections;
      let newSection = {
        title: newSectionTitle,
        description: "",
        mobileQuestions: [],
        webQuestions: [],
        printQuestions: [],
      } as Section;

      sections.splice(belowSectionIndex + 1, 0, newSection);
      refreshFormDef("add section", belowSectionIndex);
    }
  };

  const renameSection = (sectionIndex: number, sectionTitle: string) => {
    formDefinition.pages[0].sections[sectionIndex].title = sectionTitle;
    refreshFormDef("rename section");
  };

  const deleteSection = (sectionIndex: number, areYouSure: boolean = false) => {
    if (!areYouSure) {
      dialog.showYesNo("Delete Section", `Are you sure you want to delete ${formDefinition.pages[0].sections[sectionIndex].title} section? This will also delete all questions within the section.`, () => deleteSection(sectionIndex, true));
    } else {
      let sections = formDefinition.pages[0].sections;
      let deleted = sections.splice(sectionIndex, 1)[0];
      // delete all the questions from the section too
      formDefinition.questionLibrary.questions = formDefinition.questionLibrary.questions.filter(q => deleted.mobileQuestions.indexOf(q.id) === -1);
      refreshFormDef("delete section", sectionIndex);
    }
  };

  const reorderSections = (newSectionIndexOrder: number[]) => {
    formDefinition.pages[0].sections = newSectionIndexOrder.map(i => formDefinition.pages[0].sections[i]);
    refreshFormDef("move section");
  };

  const reorderQuestions = (details: QuestionEditDetails, newQuestionIdOrder: string[]) => {
    if (details.section) details.section.mobileQuestions = newQuestionIdOrder;
    if (details.repeatOrGroup) details.repeatOrGroup.control.controls = newQuestionIdOrder.map(id => details.repeatOrGroup.control.controls.find(c => c.questionId === id));

    refreshFormDef("move question");
  };

  const setAddQuestionOrder = (questionTitles: string[]) => {
    savePreference(PhoenixFormsDesignerAddQuestionOrder, questionTitles.join(","), null, mounted);
    _setAddQuestionOrder(questionTitles);
  };

  const getQuestionById = (id: string) => formDefinition?.questionLibrary?.questions?.find(q => q.id === id);

  const saveFormDef = (successCallback: () => void = null, errorCallback: () => void = null) => {
    setIsSavingForm(true);
    updateFormDefinition(phoenixFormId, formDefinition, () => {
      setIsSavingForm(false);
      // log in the editor that we saved the form, this helps us figure out if we should block navigation going back
      refreshFormDef("form saved");
      successCallback?.();
    }, errorCallback, mounted);
  };

  const saveFormIfChangesWorkflow = async () => {
    if (formHasUnsavedChanges()) {
      let saveSuccessful = await new Promise(resolve => saveFormDef(() => resolve(true), () => resolve(false)));
      
      if (!saveSuccessful) {
        dialog.showOk("Form save failed", "(╯°□°)╯︵ ┻━┻");
        return;
      }
    }
  };

  const onBackToFormsClicked = () => {
    history.push("/phoenixforms-designer/forms-list");
  };

  const onPromoteClicked = async () => {
    setGenericLoadingMessage("Validating Form...");
    let results = await validate("standard", true);
    setGenericLoadingMessage(null);

    let stillPromote = true;
    if (results.failed) {
      stillPromote = await new Promise(resolve => dialog.showYesNo("Form Validation - Failed", <ValidationMessages validationResult={results} />, () => resolve(true), () => resolve(false), "Override", "Close", { className: "dialog-widthfix" }));
    }

    if (!stillPromote) return;

    setGenericLoadingMessage("Getting Promote Data...");
    let promoteData = await new Promise<PromoteFormData>(resolve => getPromoteData(phoenixFormId, resolve, () => resolve(null), mounted));
    setGenericLoadingMessage(null);

    stillPromote = await new Promise(resolve => dialog.showYesNo(
      "Destination",
      <div className="promote-details">
        <div className="promote-header">Account</div>
        <div>{promoteData.accountName}</div>
        <div className="promote-header">Form</div>
        <div>{promoteData.formName ?? "New"}</div>
        <div className="promote-header">PDFs</div>
        <div>{promoteData.pdfList.map(pdf => <div>{pdf}</div>)}</div>
      </div>,
      () => resolve(true),
      () => resolve(false),
      "Promote",
      "Cancel",
      { className: "dialog-widthfix" }
    ));

    if (!stillPromote) return;

    stillPromote = await new Promise(resolve => dialog.showYesNo(
      "Confirm",
      "Are you sure?",
      () => resolve(true),
      () => resolve(false)
    ));

    if (!stillPromote) return;

    setGenericLoadingMessage("Promoting Form...");
    let promoteResponse = await new Promise<FormDesignerPromoteResponse>(resolve => promoteForm(phoenixFormId, resolve, () => resolve(null), mounted));
    setGenericLoadingMessage(null);

    if (promoteResponse) {
      dialog.showOk(
        "Success",
        <div className="promote-details">
          <div className="promote-header">Promoted Successfully</div>
          <div className="promote-header">ID:</div>
          <div>{promoteResponse.promotedFormDefinitionId}</div>
          <div className="promote-header">PDF ID(s):</div>
          {promoteResponse.promotedPdfDefinitionIds.map(id => <div>{id}</div>)}
        </div>,
        dialog.hide,
        undefined,
        { className: "dialog-widthfix" },
      );
    } else {
      dialog.showOk("Failure", "Promote Failed", dialog.hide)
    }
  };

  const copyToClipboard = (questionId: string) => {
    if (clipboard.find(q => q.id === questionId)) {
      makeToast("This item is already on your clipboard", "error");
    } else {
      let copiedQuestion = deepCopy(getQuestionById(questionId));
      let newClipboard = [copiedQuestion, ...clipboard];
      setClipboard(newClipboard);
      (window as any).formsDesginerClipboard = newClipboard;
      makeToast("Item copied to clipboard", "success");
    }
  };

  const removeFromClipboard = (clipboardIndex: number) => {
    let copied = [...clipboard];
    copied.splice(clipboardIndex, 1);
    setClipboard(copied);
    (window as any).formsDesginerClipboard = copied;
  };

  const loadNewFormDef = (formDef: FormDefinition, lastChangeType: FormsDesignerLastChangeType = "initial load") => {
    _formDefRef.current = formDef;
    refreshFormDef(lastChangeType);
    if (selectedQuestion) setSelectedQuestion(formDef.questionLibrary.questions.find(q => q.id === selectedQuestion.id));
    setGenericLoadingMessage(null);
    setFormLastLoadedAt(Date.now());
  };

  const editDataModel = async () => {

    const shouldSave = await new Promise<boolean>(resolve => {

      let dataModel = formDefinition.dataModel;
      dialog.showYesNo(
        `Editing Data Model for ${formDefinition.name}`,
        (<div className="pnx-form-list-code-editor">
          <CodeEditor
            mode="xml"
            onChange={newValue => dataModel = newValue}
            value={dataModel}
          />
        </div>),
        () => {
          formDefinition.dataModel = dataModel;
          resolve(true);
        },
        () => resolve(false),
        "Save",
        "Cancel",
        { className: "dialog-widthfix" },
      );
    });

    if (shouldSave) {
      saveFormDef();
    }
  };

  const generateNewDataModel = async () => {
    let isSure = await new Promise<boolean>(resolve => 
      dialog.showYesNo(
        "Generate Data Model",
        "This form already has a data model. If you proceed, the existing one will be destroyed and the questions may be mapped differently. Are you sure?",
        () => resolve(true),
        () => resolve(false),
      ));
    
    if (isSure) {
      await saveFormIfChangesWorkflow();

      setGenericLoadingMessage("Generating new data model...");
      generateDataModelRequest(phoenixFormId, formDef => loadNewFormDef(formDef, "generated data model"), null, mounted);
    }
  };

  const updateDataModel = async () => {
    await saveFormIfChangesWorkflow();

    setGenericLoadingMessage("Updating data model...");
    updateDataModelRequest(phoenixFormId, formDef => loadNewFormDef(formDef, "updated data model"), null, mounted);
  };

  const setIsFormPublic = async (isPublic: boolean, successCallback?: () => void) => {
    if (formDefinition.publicPreviewEnabled != isPublic) {
      if (formHasUnsavedChanges()) {
        let sure = await new Promise<boolean>(resolve => dialog.showYesNo("Change requires save", `Setting the form ${isPublic ? "public" : "private"} will save unsaved changes. Would you like to continue?`, () => resolve(true), () => resolve(false)));
        if (!sure) return;
      }

      formDefinition.publicPreviewEnabled = isPublic;
      saveFormDef(successCallback);
    } else {
      successCallback?.();
    }
  };

  const value: DesignerProviderValue = {
    phoenixFormId,
    formDefinition,
    getQuestionById,
    setSelectedQuestion,
    selectedQuestion,
    refreshFormDef,
    formDefinitionUpdatedAt,
    addQuestionOrder,
    setAddQuestionOrder,
    lastChangeType,
    lastChangeData,
    isSavingForm,
    cmsList,
    generateNewDataModel,
    updateDataModel,
    editFns: {
      addNewQuestion,
      deleteQuestion,
      reorderSections,
      reorderQuestions,
      addNewSection,
      renameSection,
      deleteSection,
      saveFormDef,
      addListItemToQuestion,
      updateQuestionListItem,
      setQuestionListItems,
      reorderQuestionListItems,
      deleteListItemFromQuestion,
    },
    clipboard: {
      data: clipboard,
      copy: copyToClipboard,
      remove: removeFromClipboard,
    },
  };

  const validate = async (type: "standard" | "export-id", noDialog = false) => {
    await saveFormIfChangesWorkflow();

    return await new Promise<FormDefinitionValidateResponse>(resolve => (type === "standard" ? validateFormDefinition : validateFormDefinitionExportId)(phoenixFormId, validationResult => {
      if (!noDialog) dialog.showOk(`Form Validation - ${validationResult.failed ? "Failed" : "Passed"}`, <ValidationMessages validationResult={validationResult} />, null, "Close", { className: "dialog-widthfix" });
      resolve(validationResult);
    },
    null, mounted));
  };

  const openFormPreview = () => {
    window.open(`${WebConfig.orthosRoot}/webui/form-preview/${phoenixFormId}`, "_blank");
  };

  const onShareClicked = () => {
    setIsFormPublic(true, () => window.open(`${WebConfig.phoenixAnywhereUrl}/services/forms4/preview/shared/${phoenixFormId}`, "", "toolbar=1,titlebar=0,menubar=0,directories=0,scrollbars=1"));
  };

  contextRef.current = value;

  let isLoaded = !!formDefinition && !!addQuestionOrder;

  let loadingMessages: string[] = [];

  if (!isLoaded) loadingMessages.push("Loading Form Data...");
  if (isSavingForm) loadingMessages.push("Saving Form Data...");
  if (genericLoadingMessage) loadingMessages.push(genericLoadingMessage);

  let isBlocked = !!loadingMessages.length;

  let buttonDefs = [
    { onClick: saveFormDef, label: "Save Form Changes" },
    { onClick: () => validate("standard"), label: "Validate" },
    { onClick: () => validate("export-id"), label: "Validate Export Ids" },
    { onClick: openFormPreview, label: "Preview Form" },
    { onClick: editDataModel, label: "Edit Data Model"} ,
    { onClick: generateNewDataModel, label: "Generate Data Model" },
    { onClick: updateDataModel, label: "Update Data Model" },
    // kelli wanted this removed, i think we're going to want this back, so im keeping it for now (9/29/25)
    // { onClick: () => setIsFormPublic(!formDefinition.publicPreviewEnabled), label: formDefinition?.publicPreviewEnabled ? "Make Private" : "Make Public" },
    { onClick: onShareClicked, label: "Share" },
    { onClick: onBackToFormsClicked, label: "Back To Forms List" },
    { onClick: onPromoteClicked, label: "Promote" },
  ];

  let buttons: ReactNode[];

  if (collapsedButtons) {
    buttons = [<Select
                // className={ReactSelectClassName}
                placeholder={"Form Actions"}
                classNamePrefix="mueller-react-select"
                isSearchable={false}
                value="value prop?"
                options={buttonDefs.map(({ label, onClick }) => ({
                    label,
                    value: onClick,
                  }))}
                onChange={selected => selected.value()}
            />];
  } else {
    buttons = buttonDefs.map(bd => <Button disabled={isBlocked} onClick={bd.onClick}>{bd.label}</Button>);
  }

  const titleResizeObserver = () => {
    if (!headerRef.current) return;
    setCollapsedButtons(headerRef.current.offsetWidth < 1500);
  }

  const titleDivResizeListenerRef = (element: HTMLDivElement) => {
    headerRef.current = element;
    if (element) {
      headerResize.current = new ResizeObserver(titleResizeObserver);
      headerResize.current.observe(element);
    } else {
      headerResize.current.disconnect();
    }
  };

  return (
    <PhoenixFormsDesignerContext.Provider value={value}>
      <div className="phoenixforms-designer">
        <div className="app-header-single-div" ref={titleDivResizeListenerRef}>
          <div className="header-text">{formDefinition ? formDefinition?.name : "Loading Form Designer..."}</div>
          {buttons}
        </div>
        {isLoaded &&
          <MSplitter
            firstChildInitialSize="33%"
            firstChild={<PhoenixFormDesignerProps key={`k-${formLastLoadedAt}`}/>}
            secondChild={<PhoenixFormDesignerPreview />}
          />
        }
      </div>
      {!!loadingMessages.length && <LoadingCover loadingMessages={loadingMessages} />}
    </PhoenixFormsDesignerContext.Provider>
  );
}

const mapDispatchToProps = dispatch => ({
  makeToast: (text: string, type: ToastType) => responseHandler.customToast(text, type, dispatch),
  setHeaderVisibility: (visible: boolean) => dispatch(setCoreLayoutHeaderVisibility(visible)),
});

export default withRouter(connect(null, mapDispatchToProps)(PhoenixFormDesigner));
